import { storage } from '@abyss/web/tools/storage';
import get from 'lodash/get';

import { Constants, PSX_LAGOON_WEB_URL } from '../common/Constants';
import { ConstantsLagoon } from '../common/ConstantsLagoon';
import { getFeatureFlags } from '../common/Utils/getFeatureFlag';
import { isLawwGuest } from '../frontends/GuestExperience/utils';
import { LANGUAGES } from '../frontends/ProviderSearch/context/Internationalization/types';

type LagoonTableData = Record<string, any[]>;

const LAGOON_CONTEXTS = {};
const CACHED_JSON = {};

const LAGOON_BASE_URL = `https://${PSX_LAGOON_WEB_URL}`;

const validateLagoonUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    if (
      urlObj.protocol !== 'https:' ||
      !urlObj.hostname.endsWith(PSX_LAGOON_WEB_URL)
    ) {
      throw new Error(`Invalid Lagoon URL`);
    }
    // Reject path traversal, query strings, and fragments to prevent request forgery
    if (
      urlObj.pathname.includes('..') ||
      urlObj.search ||
      urlObj.hash ||
      !/^[\w/.\-]+$/.test(urlObj.pathname)
    ) {
      throw new Error(`Invalid Lagoon URL path`);
    }
    // Reconstruct the URL from the trusted base origin + validated path
    // so the fetch target is always the known Lagoon domain by construction
    const trustedUrl = new URL(urlObj.pathname, LAGOON_BASE_URL);
    return trustedUrl.href;
  } catch {
    throw new Error(`Malformed Lagoon URL`);
  }
};

const sanitizeLOB = (lobValue: string | null): string | null => {
  if (!lobValue || typeof lobValue !== 'string') return null;
  const sanitizedLOB = lobValue
    .toLowerCase()
    .trim()
    .replaceAll(/[^a-zA-Z0-9&%-]/g, '');
  return sanitizedLOB || null;
};

export const fetchJSON = (url: string) => {
  // Validate URL format and domain, and get the sanitized URL from the parsed object
  const sanitizedUrl = validateLagoonUrl(url);

  if (CACHED_JSON[sanitizedUrl]) {
    return Promise.resolve(CACHED_JSON[sanitizedUrl]);
  }

  return fetch(sanitizedUrl, { cache: 'no-cache' })
    .then((response) => response.json())
    .then((data) => {
      CACHED_JSON[sanitizedUrl] = data;
      return data;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to fetch Lagoon data from %s:',
        sanitizedUrl,
        error
      );
      return {};
    });
};

const isGuestPlanSelectionFlow = () => {
  const pathname = window?.location?.pathname?.toLowerCase() || '';
  return pathname.startsWith('/guest-plan-selection');
};

export const getLOBTargetInCookies = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const lobFromUrl = sanitizeLOB(urlParams?.get('lob'));
  const lobTarget = storage.cookie.get(Constants.LOBTARGET);
  const opLob = storage.cookie.get('OP_LOB');

  const isLawwGuestExperience = isLawwGuest();

  const guestPlanSelectionLob = isGuestPlanSelectionFlow()
    ? isLawwGuestExperience
      ? 'guest-obh'
      : 'guest-plan-selection'
    : undefined;
  const resolvedLob =
    guestPlanSelectionLob ?? lobFromUrl ?? lobTarget ?? opLob ?? 'e&i';

  return resolvedLob;
};

const mapTableData = (_, sourceData) => {
  const table = {};

  if (!sourceData || !Array.isArray(sourceData)) {
    return table;
  }

  sourceData.forEach((dataObj) => {
    const key = dataObj.key || dataObj.id || dataObj.rowNum;
    if (key) {
      table[key] = dataObj;
    }
  });

  return table;
};

export const getCloudData = async (lob, env, locale) => {
  const enLocaleUrl = `https://${PSX_LAGOON_WEB_URL}/${lob}/${env}/en/en.json`;

  // ENGLISH LOCALE
  if (locale === 'en') {
    const enLocaleData = await fetchJSON(enLocaleUrl);

    const mappedData = {};

    Object.keys(enLocaleData).forEach((tableKey) => {
      mappedData[tableKey] = mapTableData(
        tableKey,
        enLocaleData[tableKey] || []
      );
    });

    return mappedData;
  }

  // OTHER LOCALES
  const mappedData = {};

  const localeUrl = `https://${PSX_LAGOON_WEB_URL}/${lob}/${env}/${locale}/${locale}.json`;
  const tablesUsingEnLocale = new Set(['feature-flags', 'config']);

  const [localeData, enLocaleData] = await Promise.all([
    fetchJSON(localeUrl),
    fetchJSON(enLocaleUrl),
  ]);

  // fall back to English if locale IS neither en nor es and corresponding feature FLAG is not enabled
  if (locale !== 'en' && locale !== 'es') {
    const [
      isBurmeseEnabled,
      isCreoleEnabled,
      isTraditionalChineseEnabled,
      isVietnameseEnabled,
      isIlocanoEnabled,
      isKoreanEnabled,
    ]: boolean[] = getFeatureFlags(
      enLocaleData[Constants.LAGOON_TABLE.FEATURE_FLAGS] || [],
      [
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_BURMESE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_CREOLE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_TRADITIONAL_CHINESE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIETNAMESE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_ILOCANO_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_KOREAN_LANGUAGE,
      ]
    );
    const supportedLanguages: string[] = [
      'en',
      'es',
      ...(isBurmeseEnabled ? ['my'] : []),
      ...(isCreoleEnabled ? ['ht'] : []),
      ...(isTraditionalChineseEnabled ? ['zh'] : []),
      ...(isVietnameseEnabled ? ['vi'] : []),
      ...(isIlocanoEnabled ? ['ic'] : []),
      ...(isKoreanEnabled ? ['ko'] : []),
    ];
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
      JSON.stringify(
        Array.from(supportedLanguages.map((lang) => LANGUAGES[lang].locale))
      )
    );

    if (!supportedLanguages.includes(locale)) {
      const mappedData = {};

      Object.keys(enLocaleData).forEach((tableKey) => {
        mappedData[tableKey] = mapTableData(
          tableKey,
          enLocaleData[tableKey] || []
        );
      });

      return mappedData;
    }
  }

  const mergeTableData = (localeTable, enLocaleTable) => {
    const mergedTable = { ...mapTableData('', enLocaleTable) };

    const localeArray = Array.isArray(localeTable) ? localeTable : [];
    localeArray.forEach((dataObj) => {
      const key = dataObj.key || dataObj.id;
      if (key) {
        mergedTable[key] = dataObj;
      }
    });
    return mergedTable;
  };

  const lobTableKeys = Object.keys(enLocaleData);
  lobTableKeys.forEach((tableKey) => {
    const localeTable = localeData[tableKey] || [];
    const enLocaleTable = enLocaleData[tableKey] || [];

    let tableData = {};

    if (tablesUsingEnLocale.has(tableKey)) {
      tableData = mapTableData(tableKey, enLocaleTable);
    } else {
      if (Array.isArray(localeTable) && Array.isArray(enLocaleTable)) {
        tableData = mergeTableData(localeTable, enLocaleTable);
      } else {
        tableData = mapTableData(
          tableKey,
          localeTable.length ? localeTable : enLocaleTable
        );
      }
    }

    if (mappedData[tableKey]) {
      mappedData[tableKey] = { ...mappedData[tableKey], ...tableData };
    } else {
      mappedData[tableKey] = tableData;
    }
  });
  return mappedData;
};

export const getMappedData = (
  guestPlanSelectionData: LagoonTableData,
  lobData: LagoonTableData,
  shouldCallGuestPlanSelectionData: boolean
) => {
  const mappedData = {};
  // Process guest plan selection data when shouldCallGuestPlanSelectionData is true
  if (
    shouldCallGuestPlanSelectionData &&
    Object.keys(guestPlanSelectionData).length > 0
  ) {
    updateMappedData(mappedData, guestPlanSelectionData);
  }
  // Then process LOB data (overrides existing mapped data)
  updateMappedData(mappedData, lobData);
  return mappedData;
};

const isValidLagoonData = (data) =>
  data && typeof data === 'object' && !Array.isArray(data);

export const updateMappedData = (
  mappedData: Object,
  lagoonData: LagoonTableData
) => {
  if (!isValidLagoonData(lagoonData)) return;
  Object.keys(lagoonData).forEach((tableKey) => {
    const tableData = mapTableData(tableKey, lagoonData[tableKey] || []);
    if (mappedData[tableKey]) {
      mappedData[tableKey] = { ...mappedData[tableKey], ...tableData };
    } else {
      mappedData[tableKey] = tableData;
    }
  });
};
export const updateLocaleMappedData = (
  mappedData: Object,
  lagoonData: LagoonTableData,
  enLagoonData: LagoonTableData,
  tablesUsingEnLocale: Set<string>
) => {
  if (!isValidLagoonData(lagoonData) || !isValidLagoonData(enLagoonData))
    return;
  Object.keys(enLagoonData).forEach((tableKey) => {
    const localeTable = lagoonData[tableKey] || [];
    const enLocaleTable = enLagoonData[tableKey] || [];
    let tableData = {};
    if (tablesUsingEnLocale.has(tableKey)) {
      tableData = mapTableData(tableKey, enLocaleTable);
    } else {
      if (Array.isArray(localeTable) && Array.isArray(enLocaleTable)) {
        tableData = mergeTableData(localeTable, enLocaleTable);
      } else {
        tableData = mapTableData(
          tableKey,
          localeTable.length ? localeTable : enLocaleTable
        );
      }
    }
    if (mappedData[tableKey]) {
      mappedData[tableKey] = { ...mappedData[tableKey], ...tableData };
    } else {
      mappedData[tableKey] = tableData;
    }
  });
};

export const mergeTableData = (localeTable, enLocaleTable) => {
  const mergedTable = { ...mapTableData('', enLocaleTable) };

  const localeArray = Array.isArray(localeTable) ? localeTable : [];
  localeArray.forEach((dataObj) => {
    const key = dataObj.key || dataObj.id;
    if (key) {
      mergedTable[key] = dataObj;
    }
  });
  return mergedTable;
};

export const getCloudDataV2 = async (lob, env, locale) => {
  const guestPlanSelectionLob = 'guest-plan-selection';
  const guestPlanSelectionUrl = `https://${PSX_LAGOON_WEB_URL}/${guestPlanSelectionLob}/${env}/en/en.json`;
  const enLocaleUrl = `https://${PSX_LAGOON_WEB_URL}/${lob}/${env}/en/en.json`;
  const isLawwGuestExperience = isLawwGuest();
  // call guest plan selection lagoon data only if LOB is guest-*.
  // For LAWW guests, skip guest-plan-selection fetch entirely and use guest-obh only.
  const shouldCallGuestPlanSelectionData =
    lob !== 'guest-plan-selection' &&
    !isLawwGuestExperience &&
    lob?.split('-')?.[0]?.toLowerCase() === 'guest';

  const [enLocaleData, guestPlanSelectionEnData] = await Promise.all([
    fetchJSON(enLocaleUrl),
    shouldCallGuestPlanSelectionData ? fetchJSON(guestPlanSelectionUrl) : {},
  ]);

  // ENGLISH LOCALE
  if (locale === 'en') {
    return getMappedData(
      guestPlanSelectionEnData,
      enLocaleData,
      shouldCallGuestPlanSelectionData
    );
  }

  // fall back to English if locale is neither en nor es and corresponding feature flag is not enabled
  if (!['en', 'es'].includes(locale)) {
    const [
      isBurmeseEnabled,
      isCreoleEnabled,
      isTraditionalChineseEnabled,
      isVietnameseEnabled,
      isIlocanoEnabled,
      isKoreanEnabled,
    ]: boolean[] = getFeatureFlags(
      enLocaleData[Constants.LAGOON_TABLE.FEATURE_FLAGS] || [],
      [
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_BURMESE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_CREOLE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_TRADITIONAL_CHINESE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIETNAMESE_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_ILOCANO_LANGUAGE,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_KOREAN_LANGUAGE,
      ]
    );
    const supportedLanguages: string[] = [
      'en',
      'es',
      ...(isBurmeseEnabled ? ['my'] : []),
      ...(isCreoleEnabled ? ['ht'] : []),
      ...(isTraditionalChineseEnabled ? ['zh'] : []),
      ...(isVietnameseEnabled ? ['vi'] : []),
      ...(isIlocanoEnabled ? ['ic'] : []),
      ...(isKoreanEnabled ? ['ko'] : []),
    ];

    const existingSupported = storage.session.get(
      Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES
    );
    const existingSupportedArray = existingSupported
      ? JSON.parse(existingSupported)
      : [];

    if (
      !existingSupported ||
      existingSupportedArray.length < supportedLanguages.length
    ) {
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
        JSON.stringify(
          Array.from(supportedLanguages.map((lang) => LANGUAGES[lang].locale))
        )
      );
    }

    if (!supportedLanguages.includes(locale)) {
      return getMappedData(
        guestPlanSelectionEnData,
        enLocaleData,
        shouldCallGuestPlanSelectionData
      );
    }
  }

  // OTHER LOCALES
  const guestPlanSelectionLocaleUrl = `https://${PSX_LAGOON_WEB_URL}/${guestPlanSelectionLob}/${env}/${locale}/${locale}.json`;
  const localeUrl = `https://${PSX_LAGOON_WEB_URL}/${lob}/${env}/${locale}/${locale}.json`;
  const tablesUsingEnLocale = new Set(['feature-flags', 'config']);

  const [localeData, guestPlanSelectionLocaleData] = await Promise.all([
    fetchJSON(localeUrl),
    shouldCallGuestPlanSelectionData
      ? fetchJSON(guestPlanSelectionLocaleUrl)
      : {},
  ]);

  const mappedData = {};

  // Process guest plan selection data when shouldCallGuestPlanSelectionData is true
  if (
    shouldCallGuestPlanSelectionData &&
    Object.keys(guestPlanSelectionLocaleData).length > 0
  ) {
    updateLocaleMappedData(
      mappedData,
      guestPlanSelectionLocaleData,
      guestPlanSelectionEnData,
      tablesUsingEnLocale
    );
  }
  // Then process LOB data (overrides existing mapped data)
  updateLocaleMappedData(
    mappedData,
    localeData,
    enLocaleData,
    tablesUsingEnLocale
  );

  return mappedData;
};

export const getTokenizedValue = (value, data) => {
  if (data && /{{[a-zA-Z0-9_]+}}/.test(value)) {
    const stringMatches = value.match(/{{([a-zA-Z0-9_]+)}}/g);

    let newValue = value;
    stringMatches.forEach((match) => {
      const dataMatch = match.replace('{{', '').replace('}}', '');
      newValue = newValue.replace(match, data[dataMatch] || '');
    });

    return newValue;
  }

  return value;
};

const getLagoonTableFns = (tableData) => {
  const tableFunctions = {};

  Object.keys(tableData).forEach((tableKey) => {
    const tableFunction = (key, data, keyPath) => {
      const values = tableData[tableKey] || {};

      if (key) {
        let value = values[key];
        if (keyPath) {
          value = get(value, keyPath);
        } else if (typeof value === 'object') {
          const objKeys = Object.keys(value);
          if (objKeys.length === 2) {
            const valueKey = objKeys.find((objKey) => objKey !== 'key');
            value = value[valueKey!];
          }
        }
        return getTokenizedValue(value, data);
      }

      const objectValues = Object.values(values);
      return objectValues.length > 0 ? objectValues : [];
    };
    tableFunctions[tableKey] = tableFunction;
  });

  return tableFunctions;
};

const setLagoonContext = (app, lob, env, hostEnv, user, events, locale) =>
  getCloudDataV2(lob, env, locale).then((cloudData) => {
    const tableFns = getLagoonTableFns(cloudData);
    const normalizedLOB = app.replace('psx-web-', '').replace('psx-web', 'eni');
    const finalApp = tableFns['globallink']
      ? `psx-web-transperfect-${normalizedLOB}`
      : app;

    LAGOON_CONTEXTS[`${finalApp}.${env}.${locale}`] = tableFns;
    if (events) {
      if (events.onReady) {
        events.onReady();
      }

      if (events.onData) {
        events.onData(tableFns);
      }
    }

    return tableFns;
  });

export const getLagoonTable = (lagoonContext, tablePath) => {
  if (lagoonContext) {
    const tableFn = lagoonContext[tablePath];
    if (tableFn) {
      return (key, data) => tableFn(key, data);
    }

    return () => [];
  }

  return () => [];
};

export const lagoon = (app, lob, env, hostEnv, locale) => ({
  initialize: (user, events) =>
    setLagoonContext(app, lob, env, hostEnv, user, events, locale),
});
